import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { apiClient } from '@/lib/apiClient'

interface WhatsappIntelligence {
  id: string
  threadId: string
  direction: string
  topicTags: string[]
  sentiment: string | null
  summary: string | null
  analyzedAt: string | null
  createdAt: string
}

interface ProposedPlaybook {
  id: string
  title: string
  opportunityId: string | null
  reviewStatus: string
  createdAt: string
}

type Tab = 'whatsapp' | 'playbooks'

export function ConversationIntelligencePage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('whatsapp')
  const [whatsapp, setWhatsapp] = useState<WhatsappIntelligence[]>([])
  const [playbooks, setPlaybooks] = useState<ProposedPlaybook[]>([])
  const [loadError, setLoadError] = useState(false)
  const [reviewing, setReviewing] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  function flash(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const loadWhatsapp = useCallback(async () => {
    try {
      const res = await apiClient.get<WhatsappIntelligence[]>('/coaching/whatsapp-intelligence')
      setWhatsapp(res.data)
    } catch {
      setLoadError(true)
    }
  }, [])

  const loadPlaybooks = useCallback(async () => {
    try {
      const res = await apiClient.get<ProposedPlaybook[]>('/coaching/proposed-playbooks')
      setPlaybooks(res.data)
    } catch {
      setLoadError(true)
    }
  }, [])

  useEffect(() => {
    if (tab === 'whatsapp') void loadWhatsapp()
    else void loadPlaybooks()
  }, [tab, loadWhatsapp, loadPlaybooks])

  const review = async (id: string, status: string) => {
    setReviewing(id)
    try {
      await apiClient.post(`/coaching/proposed-playbooks/${id}/review`, { status })
      void loadPlaybooks()
    } catch {
      flash(t('conversationIntelligence.error_review'))
    } finally {
      setReviewing(null)
    }
  }

  if (loadError) {
    return <p className="text-red-600 p-6">{t('conversationIntelligence.error_load')}</p>
  }

  const tabClass = (active: boolean) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
      active
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`

  return (
    <div className="space-y-6 p-6">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      <div>
        <h1 className="text-2xl font-semibold">{t('conversationIntelligence.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('conversationIntelligence.subtitle')}</p>
      </div>

      <div className="flex gap-0 border-b">
        <button className={tabClass(tab === 'whatsapp')} onClick={() => setTab('whatsapp')}>
          {t('conversationIntelligence.whatsapp_tab')}
        </button>
        <button className={tabClass(tab === 'playbooks')} onClick={() => setTab('playbooks')}>
          {t('conversationIntelligence.playbooks_tab')}
        </button>
      </div>

      {tab === 'whatsapp' && (
        <>
          {whatsapp.length === 0 ? (
            <p className="text-muted-foreground">{t('conversationIntelligence.empty_whatsapp')}</p>
          ) : (
            <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-card border-b">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-foreground">
                      {t('conversationIntelligence.col_thread')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-foreground">
                      {t('conversationIntelligence.col_direction')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-foreground">
                      {t('conversationIntelligence.col_sentiment')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-foreground">
                      {t('conversationIntelligence.col_tags')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-foreground">
                      {t('conversationIntelligence.col_analyzed')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {whatsapp.map(row => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{row.threadId}</td>
                      <td className="px-4 py-2 text-muted-foreground">{row.direction}</td>
                      <td className="px-4 py-2">
                        {row.sentiment
                          ? t(
                              `conversationIntelligence.sentiment_${row.sentiment}` as Parameters<typeof t>[0],
                              { defaultValue: row.sentiment },
                            )
                          : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {row.topicTags.map(tag => (
                            <span
                              key={tag}
                              className="bg-blue-950/40 text-blue-300 text-xs px-1.5 py-0.5 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {row.analyzedAt
                          ? new Date(row.analyzedAt).toLocaleDateString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'playbooks' && (
        <>
          {playbooks.length === 0 ? (
            <p className="text-muted-foreground">{t('conversationIntelligence.empty_playbooks')}</p>
          ) : (
            <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-card border-b">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-foreground">
                      {t('conversationIntelligence.col_title')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-foreground">
                      {t('conversationIntelligence.col_status')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-foreground">
                      {t('conversationIntelligence.col_created')}
                    </th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {playbooks.map(pb => (
                    <tr key={pb.id} className="border-b last:border-0">
                      <td className="px-4 py-2 font-medium">{pb.title}</td>
                      <td className="px-4 py-2">
                        <span className="text-xs bg-yellow-950/40 text-yellow-300 px-2 py-0.5 rounded-full">
                          {t(
                            `conversationIntelligence.status_${pb.reviewStatus}` as Parameters<typeof t>[0],
                            { defaultValue: pb.reviewStatus },
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {new Date(pb.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2">
                        {pb.reviewStatus === 'PENDING' && (
                          <div className="flex gap-2">
                            <button
                              className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 disabled:opacity-50"
                              disabled={reviewing === pb.id}
                              onClick={() => void review(pb.id, 'APPROVED')}
                            >
                              {t('conversationIntelligence.approve')}
                            </button>
                            <button
                              className="text-xs border border-red-700 text-red-600 px-2 py-1 rounded hover:bg-red-950/40 disabled:opacity-50"
                              disabled={reviewing === pb.id}
                              onClick={() => void review(pb.id, 'REJECTED')}
                            >
                              {t('conversationIntelligence.reject')}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
